/* TOFFEE: slow seamless partner loop on desktop and mobile.
 * Click/tap the strip to pause; no visible play/pause symbols.
 * Respect reduced motion. No customer data, storage or network requests.
 */
if (!customElements.get('toffee-partner-strip')) {
  customElements.define('toffee-partner-strip', class extends HTMLElement {
    connectedCallback() {
      // Wait for the section children when the theme editor inserts fresh markup.
      queueMicrotask(() => this.initialize());
    }

    initialize() {
      if (!this.isConnected || this.controller) return;
      const viewport = this.querySelector('.toffee-trust-marquee__viewport');
      const track = this.querySelector('.toffee-trust-marquee__track');
      const group = track?.querySelector('.toffee-trust-marquee__group');
      const button = this.querySelector('.toffee-trust-marquee__pause');
      if (!viewport || !track || !group || !button || group.children.length < 2) return;

      this.controller = new AbortController();
      this.copy = group.cloneNode(true);
      this.copy.classList.add('toffee-trust-marquee__group--copy');
      this.copy.setAttribute('aria-hidden', 'true');
      this.copy.setAttribute('inert', '');
      // Keep Shopify block editing and document identifiers on the original only.
      [this.copy, ...this.copy.querySelectorAll('*')].forEach((element) => {
        element.removeAttribute('id');
        element.removeAttribute('data-shopify-editor-block');
        element.removeAttribute('data-shopify-editor-section');
      });
      this.copy.querySelectorAll('img').forEach((image) => {
        image.loading = 'eager';
      });
      track.append(this.copy);

      // Remove the old icon artwork. The native button covers the strip without
      // a visible surface; its accessible name explains click/tap/keyboard use.
      button.replaceChildren();
      const updateLabel = () => {
        const label = this.hasAttribute('data-paused')
          ? button.dataset.playLabel : button.dataset.pauseLabel;
        button.setAttribute('aria-label', label);
        button.setAttribute('title', label);
        button.setAttribute('aria-pressed', String(this.hasAttribute('data-paused')));
      };
      button.addEventListener('click', () => {
        this.toggleAttribute('data-paused');
        updateLabel();
      }, { signal: this.controller.signal });
      updateLabel();
      button.hidden = false;
      // Each group spans at least the viewport, so the next copy always fills
      // the screen. Keep the mobile 48s loop; larger desktops move at ~14px/s.
      const measure = () => {
        this.measureFrame = null;
        if (!this.isConnected || !this.controller) return;
        const viewportWidth = viewport.clientWidth;
        if (!viewportWidth) return;
        this.style.setProperty('--toffee-trust-viewport-width', `${viewportWidth}px`);
        const groupWidth = group.getBoundingClientRect().width;
        const mobile = window.matchMedia('(max-width: 699px)').matches;
        const duration = mobile ? 48 : Math.max(48, groupWidth / 14);
        this.style.setProperty('--toffee-trust-duration', `${duration.toFixed(3)}s`);
      };
      const scheduleMeasure = () => {
        if (!this.controller || this.measureFrame != null) return;
        this.measureFrame = requestAnimationFrame(measure);
      };
      measure();
      this.classList.add('is-ready');
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(scheduleMeasure);
        this.resizeObserver.observe(viewport);
        this.resizeObserver.observe(group);
      }
      window.addEventListener('resize', scheduleMeasure, { signal: this.controller.signal });
      document.fonts?.ready.then(scheduleMeasure);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.resizeObserver = null;
      if (this.measureFrame != null) cancelAnimationFrame(this.measureFrame);
      this.measureFrame = null;
      this.controller?.abort();
      this.controller = null;
      this.copy?.remove();
      this.copy = null;
      this.classList.remove('is-ready');
      const button = this.querySelector('.toffee-trust-marquee__pause');
      if (button) button.hidden = true;
    }
  });
}
